import type { Residency } from '@salonomy/types';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useEndResidency } from '../../data/practitioner.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS11 — end a chair-rental residency (salon-side; owner/manager). Destructive-with-future-bookings, so
 * it uses the typed-confirmation idiom (ConfirmModal) exactly like StaffModal deactivation / cancel-series.
 * The consequence text is the brief's promise stated plainly: deactivating the seat detaches FUTURE
 * availability while the salon's history AND the practitioner's own sealed client book are preserved
 * (ADR-0012 §4). Keyed on the residency's staffId in this tenant. Nothing here is recomputed — the end
 * is a single server call and the data layer re-reads the roster.
 */
export function EndResidencyDialog({
  residency,
  onClose,
}: {
  residency: Residency | null;
  onClose: () => void;
}) {
  const end = useEndResidency();
  const toast = useToast();

  async function confirm() {
    if (!residency) return;
    try {
      await end.mutateAsync(residency.staffId);
      toast.success(`Residency at ${residency.salonName} ended`);
      onClose();
    } catch {
      toast.error('Could not end this residency.');
    }
  }

  return (
    <ConfirmModal
      open={Boolean(residency)}
      title="End residency"
      confirmLabel="End residency"
      // The salon name is the typed phrase — a deliberate, legible guard against an accidental click.
      confirmPhrase={residency?.salonName}
      busy={end.isPending}
      onConfirm={confirm}
      onClose={onClose}
    >
      <p style={{ margin: '0 0 8px' }}>
        This deactivates the seat at <strong>{residency?.salonName}</strong> and detaches its future
        availability — no new bookings will route to this chair.
      </p>
      <p style={{ margin: 0 }}>
        The salon’s history is preserved, and the practitioner’s own client book stays with them —
        ending a residency never deletes records or moves a sealed book.
      </p>
    </ConfirmModal>
  );
}
